import { RiArrowRightSLine, RiRestartLine } from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldDescription, FieldError } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { Label } from "@screener/uikit/components/label";
import { PanelFooter } from "@screener/uikit/components/panel";
import { SegmentedControl } from "@screener/uikit/components/segmented-control";
import { StatusDot } from "@screener/uikit/components/status-dot";
import { Switch } from "@screener/uikit/components/switch";
import { Tag } from "@screener/uikit/components/tag";
import { cn } from "@screener/uikit/lib/utils";
import type { RefObject } from "react";
import { SettingsGroupRow, SettingsRow } from "@/settings/settings-row";
import { terminalName } from "./terminal-action";
import type { TerminalSettingsHint } from "./terminal-controls";
import { MappingTable } from "./terminal-mapping-table";
import type { TerminalKind } from "./terminal-settings";
import {
  type MappingDraftRow,
  type MetascalpDraftRow,
  type ParsedGroups,
  parseGroupsInput,
  type TerminalDraftErrors,
  type TerminalSettingsDraft,
} from "./terminal-settings-draft";

/**
 * Содержимое настроек терминала (§10 1.36, §127 п. 7). Пишет только в
 * хранилище настроек браузера — ни поток, ни запросный канал отсюда не
 * вызываются. Чистая разметка (тестируется `renderToStaticMarkup`, как
 * `RuleFormContent`); черновик и «Сохранить» держит раздел страницы настроек.
 * Возвращает строки панели вместе с подвалом: рамку и шапку рисует раздел.
 */

const TERMINAL_OPTIONS: ReadonlyArray<{ value: TerminalKind; label: string }> = [
  { value: "tigertrade", label: "TigerTrade" },
  { value: "metascalp", label: "MetaScalp" },
];

export interface TerminalSettingsContentProps {
  draft: TerminalSettingsDraft;
  errors: TerminalDraftErrors;
  onChange: (patch: Partial<Omit<TerminalSettingsDraft, "rows" | "metascalpRows">>) => void;
  onRowChange: (key: string, patch: Partial<Pick<MappingDraftRow, "e" | "m">>) => void;
  onMetascalpRowChange: (
    key: string,
    patch: Partial<Pick<MetascalpDraftRow, "exchange" | "market">>,
  ) => void;
  /** Поиск порта MetaScalp по кнопке «Найти»; результат — `findStatus`. */
  onFindPort: () => void;
  /** Итог последнего поиска словами; пусто — не искали. */
  findStatus?: string;
  /** Поиск идёт: «Найти» недоступна, повторный поиск не запускается. */
  finding?: boolean;
  onSave: () => void;
  /** Черновик совпадает с сохранённым: «Сохранить» недоступна, «Отменить» не показана. */
  unchanged?: boolean;
  /** «Отменить»: черновик возвращается к сохранённому. Без него кнопки нет. */
  onCancel?: () => void;
  /** Сохранённое состояние словами для подвала: «TigerTrade · 127.0.0.1:7819». */
  statusText?: string;
  /** Сохранение прошло: рядом с кнопкой «сохранено», как у Telegram; гаснет при следующей правке. */
  savedNotice?: boolean;
  /** Сохранение отклонено: текст ошибки под кнопкой. */
  saveError?: string;
  /** Повод открытия: `groups` — пояснение у поля набора и фокус в него. */
  hint?: TerminalSettingsHint;
  /** Ссылка на поле набора: страница ставит в него фокус по поводу `groups`. */
  groupsInputRef?: RefObject<HTMLInputElement | null>;
}

export function TerminalSettingsContent({
  draft,
  errors,
  onChange,
  onRowChange,
  onMetascalpRowChange,
  onFindPort,
  findStatus = "",
  finding = false,
  onSave,
  unchanged = false,
  onCancel,
  statusText,
  savedNotice = false,
  saveError,
  hint,
  groupsInputRef,
}: TerminalSettingsContentProps) {
  const hasErrors = Object.keys(errors).length > 0;
  const name = terminalName(draft.terminal);
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-4">
        <SegmentedControl
          label="Терминал"
          options={TERMINAL_OPTIONS.map((option) => ({
            value: option.value,
            label: (
              <>
                <StatusDot
                  state={option.value === draft.terminal && draft.enabled ? "online" : "off"}
                />
                {option.label}
              </>
            ),
          }))}
          value={draft.terminal}
          onValueChange={(terminal) => onChange({ terminal })}
          className="rounded-lg border border-border bg-background p-0.5"
        />
        <Label
          className={cn(
            "flex items-center gap-2.5 text-xs font-medium",
            draft.enabled ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {draft.enabled ? "Включён" : "Выключен"}
          <Switch
            aria-label={`Включить ${name}`}
            checked={draft.enabled}
            onCheckedChange={(checked) => onChange({ enabled: checked })}
          />
        </Label>
      </div>

      {draft.terminal === "metascalp" ? (
        <MetascalpFields
          draft={draft}
          errors={errors}
          onChange={onChange}
          onMetascalpRowChange={onMetascalpRowChange}
          onFindPort={onFindPort}
          findStatus={findStatus}
          finding={finding}
        />
      ) : (
        <>
          <SettingsGroupRow
            label="Подключение"
            hint={`Адрес и порт, на которых ${name} принимает команды.`}
          >
            <div className="flex items-start gap-2">
              <Field invalid={Boolean(errors.host)} className="min-w-0 max-w-[220px] flex-1">
                <Input
                  id="terminal-host"
                  aria-label="Адрес"
                  value={draft.host}
                  autoComplete="off"
                  className="tabular-nums"
                  aria-invalid={errors.host ? true : undefined}
                  onChange={(event) => onChange({ host: event.target.value })}
                />
                {errors.host ? <FieldError match>{errors.host}</FieldError> : null}
              </Field>
              <span aria-hidden="true" className="pt-1 text-muted-foreground">
                :
              </span>
              <Field invalid={Boolean(errors.port)} className="w-[76px] shrink-0">
                <Input
                  id="terminal-port"
                  aria-label="Порт"
                  value={draft.port}
                  inputMode="numeric"
                  autoComplete="off"
                  className="tabular-nums"
                  aria-invalid={errors.port ? true : undefined}
                  onChange={(event) => onChange({ port: event.target.value })}
                />
                {errors.port ? <FieldError match>{errors.port}</FieldError> : null}
              </Field>
            </div>
          </SettingsGroupRow>

          <SettingsRow
            label="Группы окон"
            htmlFor="terminal-groups"
            invalid={Boolean(errors.groups)}
            hint={
              <>
                {hint === "groups" ? (
                  <span className="block font-medium text-foreground">Выберите группы окон</span>
                ) : null}
                Допустимы группы A–Z и 0–11, через запятую или пробел. Порядок в строке — порядок
                круга: каждая отправка уходит в следующую группу, после последней — снова в первую.
              </>
            }
          >
            <Input
              id="terminal-groups"
              ref={groupsInputRef}
              value={draft.groups}
              placeholder="1, 2, 3"
              autoComplete="off"
              aria-invalid={errors.groups ? true : undefined}
              onChange={(event) => onChange({ groups: event.target.value })}
            />
            {errors.groups ? <FieldError match>{errors.groups}</FieldError> : null}
            <GroupsCircle parsed={parseGroupsInput(draft.groups)} />
          </SettingsRow>

          <MappingTable
            title="Соответствие бирж и рынков"
            description="Значения e и m, которые TigerTrade знает у этой биржи. Пустые e или m — соответствия нет, и действия «открыть в TigerTrade» у такого рынка не будет."
            columns={["e", "m"]}
            rows={draft.rows.map((row) => ({
              key: row.key,
              first: row.e,
              second: row.m,
              flagged: (row.e.trim() === "") !== (row.m.trim() === ""),
            }))}
            onChange={(key, patch) =>
              onRowChange(key, {
                ...(patch.first === undefined ? {} : { e: patch.first }),
                ...(patch.second === undefined ? {} : { m: patch.second }),
              })
            }
            flaggedNote={(labels) =>
              `${labels.join(", ")}: заполнено только одно из e и m — соответствия пока нет.`
            }
          />
        </>
      )}

      <PanelFooter>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {statusText ? (
            <span className="text-xs text-muted-foreground tabular-nums">{statusText}</span>
          ) : null}
          {savedNotice ? <span className="text-xs text-muted-foreground">сохранено</span> : null}
          {saveError ? (
            <p role="alert" className="text-xs text-destructive">
              {saveError}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {!unchanged && onCancel ? (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Отменить
            </Button>
          ) : null}
          <Button
            size="sm"
            className="font-semibold"
            onClick={onSave}
            disabled={hasErrors || unchanged}
          >
            Сохранить
          </Button>
        </div>
      </PanelFooter>
    </>
  );
}

/** Круг отправок: порядок групп и возврат к первой. Пуст — не показывается. */
function GroupsCircle({ parsed }: { parsed: ParsedGroups }) {
  const { groups, error } = parsed;
  // Набор с ошибкой кругом не рисуется: порядок разобран лишь до неверной группы, а сообщение уже на экране.
  if (error !== null || groups.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
        Круг
      </span>
      {groups.map((group, index) => (
        <span key={group} className="flex items-center gap-1.5">
          {index > 0 ? (
            <RiArrowRightSLine aria-hidden="true" className="size-3 text-muted-foreground" />
          ) : null}
          <Tag className={cn("min-w-[22px] justify-center", index === 0 && "bg-foreground/10")}>
            {group}
          </Tag>
        </span>
      ))}
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <RiRestartLine aria-hidden="true" className="size-3" />
        снова {groups[0]}
      </span>
    </div>
  );
}

function MetascalpFields({
  draft,
  errors,
  onChange,
  onMetascalpRowChange,
  onFindPort,
  findStatus,
  finding,
}: Pick<
  TerminalSettingsContentProps,
  "draft" | "errors" | "onChange" | "onMetascalpRowChange" | "onFindPort"
> & { findStatus: string; finding: boolean }) {
  return (
    <>
      <SettingsRow
        label="Порт MetaScalp"
        htmlFor="terminal-metascalp-port"
        invalid={Boolean(errors.metascalpPort)}
        hint="Пусто — порт ищется сам среди 17845–17855 при отправке. Браузер может спросить разрешение на доступ к локальной сети — его нужно дать."
      >
        <div className="flex items-center gap-2">
          <Input
            id="terminal-metascalp-port"
            value={draft.metascalpPort}
            placeholder="Автопоиск"
            inputMode="numeric"
            autoComplete="off"
            className="max-w-[220px] tabular-nums"
            aria-invalid={errors.metascalpPort ? true : undefined}
            onChange={(event) => onChange({ metascalpPort: event.target.value })}
          />
          <Button variant="outline" disabled={finding} onClick={onFindPort}>
            Найти
          </Button>
        </div>
        {errors.metascalpPort ? <FieldError match>{errors.metascalpPort}</FieldError> : null}
        {/* Область живая и смонтирована всегда: меняется только текст, иначе читающий с экрана не услышит итог. */}
        <FieldDescription
          role="status"
          className="text-xs font-medium text-foreground empty:sr-only"
        >
          {findStatus}
        </FieldDescription>
      </SettingsRow>

      <SettingsRow
        label="Связки окон"
        htmlFor="terminal-bindings"
        invalid={Boolean(errors.bindings)}
        hint="Номера от 1 до 500, через запятую или пробел. Порядок в строке — порядок круга: каждая отправка уходит в следующую связку. Пусто — в активное окно MetaScalp."
      >
        <Input
          id="terminal-bindings"
          value={draft.bindings}
          placeholder="1, 2, 3"
          autoComplete="off"
          aria-invalid={errors.bindings ? true : undefined}
          onChange={(event) => onChange({ bindings: event.target.value })}
        />
        {errors.bindings ? <FieldError match>{errors.bindings}</FieldError> : null}
      </SettingsRow>

      <MappingTable
        title="Соответствие бирж и рынков"
        description="Коды exchange и market, которые MetaScalp знает у этой биржи. Пустые обе ячейки — соответствия нет, и действия «открыть в MetaScalp» у такого рынка не будет."
        columns={["exchange", "market"]}
        numeric
        rows={draft.metascalpRows.map((row) => ({
          key: row.key,
          first: row.exchange,
          second: row.market,
          flagged: errors.codeRows?.includes(row.key) ?? false,
        }))}
        onChange={(key, patch) =>
          onMetascalpRowChange(key, {
            ...(patch.first === undefined ? {} : { exchange: patch.first }),
            ...(patch.second === undefined ? {} : { market: patch.second }),
          })
        }
        errorSlot={
          errors.codes ? (
            <Field invalid>
              <FieldError match>{errors.codes}</FieldError>
            </Field>
          ) : null
        }
      />
    </>
  );
}
